import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, Dumbbell, Target, Sparkles, Briefcase, Heart } from "lucide-react";
import PageHero from "@/components/aevos/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuizFlow } from "@/hooks/useQuizFlow";

const PROFILES = [
  { slug: "academia", label: "Praticante de academia", Icon: Dumbbell, desc: "Treina com regularidade e quer otimizar performance e composição corporal." },
  { slug: "atleta", label: "Atleta amador", Icon: Target, desc: "Compete em modalidade(s) e busca evoluir performance com base científica." },
  { slug: "emagrecer", label: "Dificuldade de emagrecer", Icon: Sparkles, desc: "Já tentou diversas estratégias e quer entender sua biologia metabólica." },
  { slug: "executivo", label: "Executivo de alta performance", Icon: Briefcase, desc: "Rotina intensa, alta cobrança, busca energia, foco e longevidade." },
  { slug: "saude", label: "Saúde desorganizada", Icon: Heart, desc: "Quer reorganizar hábitos, exames e construir uma base sustentável." },
];

const QUESTIONS = [
  {
    id: "frequencia_treino",
    q: "Com que frequência você se exercita?",
    options: ["Não me exercito", "1–2x por semana", "3–4x por semana", "5x ou mais por semana"],
  },
  {
    id: "energia",
    q: "Como você descreveria seu nível de energia ao longo do dia?",
    options: ["Baixo o dia todo", "Cai muito à tarde", "Razoável, com oscilações", "Estável e alto"],
  },
  {
    id: "sono",
    q: "Como está seu sono nas últimas semanas?",
    options: ["Muito ruim", "Irregular", "Razoável", "Restaurador"],
  },
  {
    id: "alimentacao",
    q: "Como você descreveria sua alimentação?",
    options: ["Muito desorganizada", "Em construção", "Boa, mas com excessos", "Estruturada e consistente"],
  },
  {
    id: "exames",
    q: "Quando foram seus últimos exames laboratoriais completos?",
    options: ["Nunca / não lembro", "Mais de 2 anos", "No último ano", "Faço de 6 em 6 meses"],
  },
  {
    id: "objetivo",
    q: "Qual seu principal objetivo agora?",
    options: ["Saúde e prevenção", "Composição corporal", "Performance esportiva", "Longevidade de longo prazo"],
  },
  {
    id: "acompanhamento",
    q: "Você já fez acompanhamento médico/nutri integrado antes?",
    options: ["Nunca", "Já fiz pontualmente", "Faço atualmente", "Quero começar agora"],
  },
];

const TOTAL_STEPS = 4;
const PROGRESS_UNIT_PERCENT = 100 / TOTAL_STEPS;

const isValidProfile = (slug) => PROFILES.some((p) => p.slug === slug);

const STEP_LABELS = {
  0: () => "Selecione um perfil",
  1: (qIndex) => `Pergunta ${qIndex + 1} de ${QUESTIONS.length}`,
  2: () => "Receba seu resultado",
  3: () => "Resultado",
};

const QuizProgress = ({ step, qIndex }) => {
  const stepFraction = step === 1 ? (qIndex + 1) / QUESTIONS.length : 1;
  const percent = stepFraction * PROGRESS_UNIT_PERCENT + step * PROGRESS_UNIT_PERCENT;
  return (
    <div className="mb-10" data-testid="quiz-progress">
      <div className="flex items-center justify-between text-xs text-[#666] mb-2">
        <span>Etapa {Math.min(step + 1, TOTAL_STEPS)} de {TOTAL_STEPS}</span>
        <span>{STEP_LABELS[step](qIndex)}</span>
      </div>
      <div className="h-1 bg-[#E0E0E0] w-full">
        <div className="h-1 bg-[#00C4FF] transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const ProfileSelectStep = ({ onSelect }) => (
  <div className="grid sm:grid-cols-2 gap-4" data-testid="quiz-step-profile">
    {PROFILES.map((p) => (
      <button
        key={p.slug}
        onClick={() => onSelect(p.slug)}
        className="aevos-card p-6 text-left group flex gap-4 items-start"
        data-testid={`quiz-profile-${p.slug}`}
      >
        <div className="w-12 h-12 border border-black flex items-center justify-center group-hover:bg-[#00C4FF] group-hover:border-[#00C4FF] transition-colors flex-shrink-0">
          <p.Icon size={20} className="text-black" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-base text-black">{p.label}</h3>
          <p className="text-sm text-[#333] mt-1">{p.desc}</p>
        </div>
      </button>
    ))}
  </div>
);

const QuestionStep = ({ question, qIndex, total, onAnswer, onPrev }) => (
  <div data-testid="quiz-step-questions">
    <p className="aevos-eyebrow mb-3">Pergunta {qIndex + 1} de {total}</p>
    <h2 className="font-display font-bold text-2xl md:text-3xl text-black leading-tight">
      {question.q}
    </h2>
    <div className="mt-8 grid sm:grid-cols-2 gap-3">
      {question.options.map((opt) => (
        <button
          key={opt}
          onClick={() => onAnswer(question.id, opt)}
          className="aevos-card p-5 text-left font-display text-sm text-black hover:border-[#00C4FF] hover:bg-[#E6F8FF] transition-colors"
          data-testid={`quiz-option-${question.id}-${opt.slice(0,8).replace(/\s/g,'-').toLowerCase()}`}
        >
          {opt}
        </button>
      ))}
    </div>
    {qIndex > 0 && (
      <button
        className="mt-6 text-sm text-[#666] hover:text-black"
        onClick={onPrev}
        data-testid="quiz-prev"
      >
        ← Voltar
      </button>
    )}
  </div>
);

const LeadCaptureStep = ({ lead, setLead, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="aevos-card p-8" data-testid="quiz-step-form">
    <p className="aevos-eyebrow mb-3">Quase lá</p>
    <h2 className="font-display font-bold text-2xl md:text-3xl text-black">
      Para onde enviamos sua análise?
    </h2>
    <p className="mt-3 text-sm text-[#333]">
      Sua análise será gerada agora e enviada para o e-mail informado.
    </p>

    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="quiz-name" className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
        <Input id="quiz-name" data-testid="quiz-input-name" required value={lead.name} onChange={(e)=>setLead({...lead, name:e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label htmlFor="quiz-email" className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
        <Input id="quiz-email" data-testid="quiz-input-email" type="email" required value={lead.email} onChange={(e)=>setLead({...lead, email:e.target.value})} className="mt-1" />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="quiz-phone" className="text-xs uppercase tracking-wider font-semibold">WhatsApp</Label>
        <Input id="quiz-phone" data-testid="quiz-input-phone" required value={lead.phone} onChange={(e)=>setLead({...lead, phone:e.target.value})} placeholder="(11) 9 9999-9999" className="mt-1" />
      </div>
    </div>

    <label className="mt-5 flex items-start gap-3 text-sm text-[#333] cursor-pointer">
      <Checkbox checked={lead.consent} onCheckedChange={(v)=>setLead({...lead, consent:!!v})} data-testid="quiz-consent" className="mt-1"/>
      <span>
        Concordo com o uso dos meus dados para receber a análise educativa e contato da Clínica AEVOS, conforme a Política de Privacidade (LGPD).
      </span>
    </label>

    <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="quiz-submit">
      {loading ? (<><Loader2 size={16} className="animate-spin"/> Gerando análise...</>) : (<>Receber minha análise <ArrowRight size={16}/></>)}
    </button>

    <p className="mt-5 text-xs text-[#666] border-t border-[#E0E0E0] pt-4">
      <strong>Disclaimer:</strong> Esta análise tem fins educativos e de orientação. Não constitui diagnóstico médico. Agende sua consulta para avaliação clínica completa.
    </p>
  </form>
);

const ResultStep = ({ result, onRestart }) => (
  <div data-testid="quiz-step-result">
    <p className="aevos-eyebrow mb-3">Sua análise AEVOS</p>
    <h2 className="font-display font-bold text-3xl md:text-4xl text-black leading-tight" data-testid="quiz-result-title">
      {result.title}
    </h2>

    <div className="mt-8 grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 aevos-card p-7">
        <p className="aevos-eyebrow mb-3">Leitura personalizada</p>
        <div className="prose-aevos text-[#333] text-base leading-relaxed whitespace-pre-line" data-testid="quiz-result-analysis">
          {result.analysis}
        </div>
      </div>
      <div className="aevos-card p-7 bg-[#E6F8FF]">
        <p className="aevos-eyebrow mb-3">Próximos passos</p>
        <ul className="space-y-3">
          {(result.recommendations || []).map((r, i) => (
            <li key={`${i}-${r.slice(0, 24)}`} className="flex items-start gap-2 text-sm text-[#333]">
              <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0"/>{r}
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="mt-8 p-5 border border-[#E0E0E0] bg-white text-xs text-[#666]" data-testid="quiz-result-disclaimer">
      <strong className="text-black">Disclaimer:</strong> {result.disclaimer}
    </div>

    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/contato"><span className="btn-primary">Agendar Consulta <ArrowRight size={16} /></span></Link>
      <button onClick={onRestart} className="btn-ghost" data-testid="quiz-restart">Refazer mapeamento</button>
    </div>
  </div>
);

const QuizStep = ({ quiz }) => {
  const { step, qIndex, lead, setLead, loading, result } = quiz;
  if (step === 0) return <ProfileSelectStep onSelect={quiz.selectProfile} />;
  if (step === 1) {
    return (
      <QuestionStep
        question={QUESTIONS[qIndex]}
        qIndex={qIndex}
        total={QUESTIONS.length}
        onAnswer={quiz.answer}
        onPrev={quiz.prevQuestion}
      />
    );
  }
  if (step === 2) return <LeadCaptureStep lead={lead} setLead={setLead} loading={loading} onSubmit={quiz.submit} />;
  return result ? <ResultStep result={result} onRestart={quiz.restart} /> : null;
};

const Mapeamento = () => {
  const [params] = useSearchParams();
  const quiz = useQuizFlow(params.get("perfil") || "", isValidProfile, QUESTIONS.length);

  return (
    <div data-testid="page-mapeamento">
      <PageHero
        eyebrow="Mapeamento Metabólico · IA"
        title="Descubra seu perfil metabólico, gratuitamente."
        subtitle="Um teste breve, baseado em ciência, com leitura personalizada gerada com apoio de IA. Análise educativa — não substitui consulta médica."
      />

      <section className="section-pad">
        <div className="aevos-container max-w-4xl">
          <QuizProgress step={quiz.step} qIndex={quiz.qIndex} />
          <QuizStep quiz={quiz} />
        </div>
      </section>
    </div>
  );
};

export default Mapeamento;
